/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  GeneralModal,
  CustomTableRow,
  CustomTableCell,
  ActionTableCell,
  TimesheetPagination,
  CustomTextField,
  ContainedButton,
  OutlinedButton,
  ConfirmationModal,
  SuccessModal,
} from '../index';
import {
  Typography,
  TableContainer,
  Table,
  TableBody,
  Grid,
  IconButton,
  TextField,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useState } from 'react';
import { useEffect } from 'react';
import { apiUrl } from '../../utilities/apiUrl';
import { styled } from '@mui/material/styles';

const DisabledTextField = styled(TextField)({
  '& label.Mui-disabled': {
    color: '#84d8c5',
  },
});

export const SentMessagesModal = (props) => {
  const [messages, setMessages] = useState([]);
  const [viewMessage, setViewMessage] = useState(false);
  const entryPerPage = 16; // number of total rows per page
  const [slice, setSlice] = useState([0, entryPerPage]);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteDisabled, setDeleteDisabled] = useState(true);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [recipient, setRecipient] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [numViews, setNumViews] = useState(0);
  const [linkTitle1, setLinkTitle1] = useState('');
  const [linkTitle2, setLinkTitle2] = useState('');
  const [linkTitle3, setLinkTitle3] = useState('');
  const [linkURL1, setLinkURL1] = useState('');
  const [linkURL2, setLinkURL2] = useState('');
  const [linkURL3, setLinkURL3] = useState('');
  const [messageId, setMessageId] = useState('');
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [deleteSuccessOpen, setDeleteSuccessOpen] = useState(false);
  const [editSuccessOpen, setEditSuccessOpen] = useState(false);
  const formattedMessages = messages.map((msg) => ({
    ...msg,
    ...props.recipientList.find((emp) => emp.empid === msg.empid),
    message: msg.message.split(/\r?\n/)[0],
    links: [
      msg.message.split(/\r?\n/)[1],
      msg.message.split(/\r?\n/)[2],
      msg.message.split(/\r?\n/)[3],
    ],
  }));

  useEffect(() => {
    setViewMessage(true);
    fetch(apiUrl('/message'), {
      method: 'GET',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        setMessages(data.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [editSuccessOpen, deleteSuccessOpen]);

  // VIEW / EDIT MESSAGE HANDLER
  const handleViewMessage = (selected) => {
    const currentMessage = formattedMessages[selected];

    setMessageId(currentMessage._id);
    setSubject(currentMessage.subject);
    setRecipient(currentMessage.name);
    setMessage(currentMessage.message);
    setNumViews(currentMessage.numviews);
    setSaveDisabled(false);
    setDeleteDisabled(false);

    const undefinedCount = currentMessage.links.filter(
      (x) => x === undefined
    ).length;

    if (undefinedCount === 0) {
      setLinkTitle1(currentMessage.links[0].split('---')[0]);
      setLinkURL1(currentMessage.links[0].split('---')[1]);
      setLinkTitle2(currentMessage.links[1].split('---')[0]);
      setLinkURL2(currentMessage.links[1].split('---')[1]);
      setLinkTitle3(currentMessage.links[2].split('---')[0]);
      setLinkURL3(currentMessage.links[2].split('---')[1]);
    } else if (undefinedCount === 1) {
      setLinkTitle1(currentMessage.links[0].split('---')[0]);
      setLinkURL1(currentMessage.links[0].split('---')[1]);
      setLinkTitle2(currentMessage.links[1].split('---')[0]);
      setLinkURL2(currentMessage.links[1].split('---')[1]);
    } else if (undefinedCount === 2) {
      setLinkTitle1(currentMessage.links[0].split('---')[0]);
      setLinkURL1(currentMessage.links[0].split('---')[1]);
    }
  };

  // SAVE EDITED MESSAGE HANDLER
  const handleSave = () => {
    fetch(apiUrl('/message/' + messageId), {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        subject: subject,
        message: message
          .concat(
            `\n${linkTitle1}---${linkURL1}\n${linkTitle2}---${linkURL2}\n${linkTitle3}---${linkURL3}`
          )
          .trim(),
        numviews: numViews,
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setEditSuccessOpen(true);
        } else {
          alert(data.message);
        }
      });
  };

  // DELETE MESSAGE HANDLER
  const handleDelete = () => {
    fetch(apiUrl('/message/' + messageId), {
      method: 'DELETE',
      credentials: 'include',
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          setDeleteDisabled(true);
          setSaveDisabled(true);
          setConfirmationModalOpen(false);
          setDeleteSuccessOpen(true);
          reset();
        } else {
          alert(data.message);
        }
      });
  };

  // PAGE PAGINATION HANDLER
  const handlePageChange = (e, page) => {
    setCurrentPage(page);
    let currPage = page - 1;
    if (page === 1) {
      setSlice([0, entryPerPage]);
    } else {
      setSlice([currPage * entryPerPage, page * entryPerPage]);
    }
  };

  const reset = () => {
    setMessageId('');
    setRecipient('');
    setNumViews(0);
    setSubject('');
    setMessage('');
    setLinkTitle1('');
    setLinkTitle2('');
    setLinkTitle3('');
    setLinkURL1('');
    setLinkURL2('');
    setLinkURL3('');
  };

  return (
    <>
      <GeneralModal
        open={props.open}
        onClose={() => {
          props.onClose();
          reset();
        }}
        style={{ width: '80%' }}
      >
        <Typography align="center" sx={{ mb: 1, fontWeight: 600 }} variant="h5">
          Sent Messages Inbox
        </Typography>
        <Grid container spacing={5}>
          <Grid item xs={7} sx={{ mt: 2 }}>
            <Grid container sx={{ height: '100%', justifyContent: 'center' }}>
              <TableContainer sx={{ borderRadius: '5px', mb: 3 }}>
                <Table size="small">
                  <TableBody>
                    {formattedMessages
                      .slice(slice[0], slice[1])
                      .map((msg, index) => (
                        <CustomTableRow key={index}>
                          <CustomTableCell scope="row" value={msg.name} />
                          <CustomTableCell
                            scope="row"
                            value={
                              msg.message.length < 40
                                ? msg.message
                                : msg.message.substring(0, 40) + '...'
                            }
                          />
                          <ActionTableCell sx={{ width: 20 }}>
                            <IconButton
                              id={
                                (index + entryPerPage) * (currentPage - 1) -
                                (currentPage - 2) * index
                              }
                              size="small"
                              onClick={(e) => {
                                setViewMessage((prev) => !prev);
                                handleViewMessage(e.currentTarget.id);
                              }}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </ActionTableCell>
                        </CustomTableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Grid item sx={{ mt: 'auto' }}>
                <TimesheetPagination
                  count={
                    formattedMessages.length % entryPerPage !== 0
                      ? Math.floor(formattedMessages.length / entryPerPage) + 1
                      : Math.floor(formattedMessages.length / entryPerPage)
                  }
                  onChange={handlePageChange}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={5}>
            <Grid container sx={{ height: '100%', justifyContent: 'center' }}>
              {viewMessage ? (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={7}>
                      <DisabledTextField
                        disabled
                        fullWidth
                        label="Recipient"
                        margin="normal"
                        value={recipient}
                        inputProps={{ style: { WebkitTextFillColor: '#FFF' } }}
                      />
                    </Grid>
                    <Grid item xs={5}>
                      <CustomTextField
                        label="Number of Views"
                        type="number"
                        onChange={(e) => setNumViews(e.target.value)}
                        value={numViews}
                      />
                    </Grid>
                  </Grid>
                  <CustomTextField
                    label="Subject"
                    onChange={(e) => setSubject(e.target.value)}
                    value={subject}
                  />
                  <CustomTextField
                    label="Message"
                    onChange={(e) => setMessage(e.target.value)}
                    multiline={true}
                    value={message}
                  />
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={5}>
                      <CustomTextField
                        label="Link Title 1"
                        onChange={(e) => setLinkTitle1(e.target.value)}
                        value={linkTitle1}
                      />
                    </Grid>
                    <Grid item xs={7}>
                      <CustomTextField
                        label="Link URL 1"
                        onChange={(e) => setLinkURL1(e.target.value)}
                        value={linkURL1}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <CustomTextField
                        label="Link Title 2"
                        onChange={(e) => setLinkTitle2(e.target.value)}
                        value={linkTitle2}
                      />
                    </Grid>
                    <Grid item xs={7}>
                      <CustomTextField
                        label="Link URL 2"
                        onChange={(e) => setLinkURL2(e.target.value)}
                        value={linkURL2}
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid item xs={5}>
                      <CustomTextField
                        label="Link Title 3"
                        onChange={(e) => setLinkTitle3(e.target.value)}
                        value={linkTitle3}
                      />
                    </Grid>
                    <Grid item xs={7}>
                      <CustomTextField
                        label="Link URL 3"
                        onChange={(e) => setLinkURL3(e.target.value)}
                        value={linkURL3}
                      />
                    </Grid>
                  </Grid>
                </>
              ) : (
                <></>
              )}
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={4}>
                  <OutlinedButton
                    onClick={() => {
                      props.onClose();
                      reset();
                    }}
                    style={{ width: '100%' }}
                    btnTitle="Cancel"
                  />
                </Grid>
                <Grid item xs={4}>
                  <ContainedButton
                    disabled={deleteDisabled}
                    onClick={() => setConfirmationModalOpen(true)}
                    style={{
                      width: '100%',
                      bgcolor: '#d5615d',
                      border: deleteDisabled
                        ? '1px solid rgba(255, 255, 255, 0)'
                        : '1px solid #d5615d',
                      '&:hover': {
                        bgcolor: 'rgb(173, 59, 55) !important',
                        borderColor: 'rgb(173, 59, 55)',
                      },
                    }}
                    btnTitle="Delete"
                  />
                </Grid>
                <Grid item xs={4}>
                  <ContainedButton
                    disabled={saveDisabled}
                    onClick={handleSave}
                    style={{
                      width: '100%',
                      border: saveDisabled
                        ? '1px solid rgba(255, 255, 255, 0)'
                        : '1px solid #84d8c5',
                    }}
                    btnTitle="Save"
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </GeneralModal>
      <ConfirmationModal
        open={confirmationModalOpen}
        onClose={() => setConfirmationModalOpen(false)}
        primaryClickHandler={handleDelete}
        secondaryClickHandler={() => setConfirmationModalOpen(false)}
        title={'Delete Employee'}
        desc={`Are you sure you want to delete a message to ${recipient}?`}
        primaryBtnName={'Delete'}
        secondaryBtnName={'Cancel'}
        primaryStyle={{
          bgcolor: '#d5615d',
          border: '1px solid #d5615d',
          '&:hover': {
            bgcolor: 'rgb(173, 59, 55) !important',
            borderColor: 'rgb(173, 59, 55)',
          },
        }}
      />
      <SuccessModal
        open={deleteSuccessOpen}
        onClose={() => setDeleteSuccessOpen(false)}
        close={() => setDeleteSuccessOpen(false)}
        desc={'You have successfully deleted a message.'}
      />
      <SuccessModal
        open={editSuccessOpen}
        onClose={() => setEditSuccessOpen(false)}
        close={() => setEditSuccessOpen(false)}
        desc={'You have successfully edited a message.'}
      />
    </>
  );
};
